import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["skuId", "quantityPackSkuId", "quantityPackQuantity", "queryParamTerm", "style", "showPricingTermsSubtext"];
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useSelector } from 'react-redux';
import { ENTERPRISE, PROFESSIONAL, orderedProductLevels } from 'self-service-api/constants/ProductLevels';
import { MERCH_CARD } from '../../../constants/tracking/Locations';
import { useAppSelector } from '../../../reducers';
import { isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { calculatedPriceSelector, planAdditionalCostSelector } from '../../../selectors/PricingSelectors';
import { productLevelForCoreSeatsSelector } from '../../../selectors/ProductConfigsSelectors';
import { hasProductLineInBundledProductInventorySelector, hasProductLineSelectorInProductInventorySelector, hasProductVersionInBundledProductInventorySelector, hasProductVersionInProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { isQuantifiableHubSelector, productForSkuIdSelector } from '../../../selectors/ProductsSelectors';
import { HighlightedPriceDisplay } from '../../common/pricing/HighlightedPriceDisplay';
import SSM0004PricingTermsSubtext from '../../common/pricing/SSM0004PricingTermsSubtext';
import IncludedSeatsDisplay from '../IncludedSeatsDisplay';
import { useShowPricePerMonthPerSeat } from '../merchCardUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HighlightedMerchPriceDisplay = _ref => {
  let {
      skuId,
      quantityPackSkuId,
      quantityPackQuantity,
      queryParamTerm,
      style = {},
      showPricingTermsSubtext = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const productConfigurations = [createProductConfig({
    skuId
  })];
  if (quantityPackSkuId && quantityPackQuantity && quantityPackQuantity > 0) productConfigurations.push(createProductConfig({
    skuId: quantityPackSkuId,
    quantity: quantityPackQuantity
  }));
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const showingOnlyAdditionalCost = useSelector(state => {
    return !isUngatedForSeats && hasProductVersionInProductInventorySelector(state, {
      skuId
    });
  });
  const pricingInfo = useSelector(state => showingOnlyAdditionalCost ? planAdditionalCostSelector(state, {
    productConfigurations,
    queryParamTerm
  }) : calculatedPriceSelector(state, {
    productConfigurations,
    queryParamTerm
  }));
  const {
    productLine,
    productLevel
  } = useSelector(state => {
    return productForSkuIdSelector(state, {
      skuId
    });
  });
  const ownsProductLineViaProducts = useAppSelector(state => {
    return hasProductLineSelectorInProductInventorySelector(state, {
      productLine
    });
  });
  const ownsProductLineViaBundle = useAppSelector(state => {
    return hasProductLineInBundledProductInventorySelector(state, {
      productLine
    });
  });
  const ownsProductLine = ownsProductLineViaProducts || ownsProductLineViaBundle;
  const ownsProductVersion = useAppSelector(state => hasProductVersionInProductInventorySelector(state, {
    skuId
  }));
  const ownsProductViaBundle = useAppSelector(state => hasProductVersionInBundledProductInventorySelector(state, {
    skuId
  }));
  const ownsProduct = ownsProductVersion || ownsProductViaBundle;
  const isQuantifiableHub = useAppSelector(state => isQuantifiableHubSelector(state, {
    skuId
  }));
  const coreSeatTierOwned = useAppSelector(state => productLevelForCoreSeatsSelector(state, {
    productConfigurations: []
  }));

  // Show "No additional cost $0/mo" on Non-quantifiable hubs' pro+ cards that users own
  const showNoAdditionalCost = isUngatedForSeats && !isQuantifiableHub && ownsProduct && (productLevel === PROFESSIONAL || productLevel === ENTERPRISE);

  // If Pro customer, looking at a hub NOT OWNED, do not show "Starts at" on Starter
  // If Ent customer, looking at a hub NOT OWNED, do not show "Starts at" on Starter and Pro
  const removeStartsAtSubtext = isUngatedForSeats && !isQuantifiableHub && !ownsProductLine && orderedProductLevels.indexOf(coreSeatTierOwned) > orderedProductLevels.indexOf(productLevel);
  const showPricePerMonthPerSeat = useShowPricePerMonthPerSeat(skuId);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [removeStartsAtSubtext ? /*#__PURE__*/_jsx("div", {
      style: {
        // Aligns with other cards that has subtext in price display
        marginTop: '18px'
      },
      children: /*#__PURE__*/_jsx(HighlightedPriceDisplay, Object.assign({
        pricingInfo: pricingInfo,
        skuId: skuId,
        wrapperStyle: style,
        showingOnlyAdditionalCost: showingOnlyAdditionalCost,
        showNoAdditionalCost: showNoAdditionalCost,
        showPricePerMonthPerSeat: showPricePerMonthPerSeat,
        showNoSubtext: removeStartsAtSubtext
      }, rest))
    }) : /*#__PURE__*/_jsx(HighlightedPriceDisplay, Object.assign({
      pricingInfo: pricingInfo,
      skuId: skuId,
      wrapperStyle: style,
      showingOnlyAdditionalCost: showingOnlyAdditionalCost,
      showNoAdditionalCost: showNoAdditionalCost,
      showPricePerMonthPerSeat: showPricePerMonthPerSeat,
      showNoSubtext: removeStartsAtSubtext
    }, rest)), showPricingTermsSubtext ? /*#__PURE__*/_jsx(SSM0004PricingTermsSubtext, {
      productConfigurations: productConfigurations,
      queryParamTerm: queryParamTerm,
      selectedTermPricingInfo: pricingInfo,
      showPricePerMonthPerSeat: showPricePerMonthPerSeat,
      location: MERCH_CARD
    }) : null, /*#__PURE__*/_jsx(IncludedSeatsDisplay, {
      skuId: skuId,
      quantityPackSkuId: quantityPackSkuId,
      quantityPackQuantity: quantityPackQuantity,
      queryParamTerm: queryParamTerm
    })]
  });
};
export default HighlightedMerchPriceDisplay;